import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
    IPindrop,
    IShipmentLocation,
    IShipmentRecord,
} from '../../api/shipment/interfaces';
import { deepClone } from '../../utils/functions';
import { ISelect } from '../../utils/interfaces';
import { IShipmentTemplate } from '../../api/template/interfaces';
import { LocationDetailConstants } from './constants';
import { SHIPMENT_INITIAL_STATE } from './initialState';
import {
    ICommodityModalDetail,
    ICommodityModalErrorDetail,
    IEstimateRateDetail,
    ILocation,
    ILocationModalDetail,
    INewShipmentBasicInfo,
    IShipmentInitialState,
    ITemplateCreationData,
} from './interfaces';
import { mapTemplateToShipmentParts } from './utils/applyTemplateMapper';
import { dispatchManagerFeatures } from '../../featureFlags/dispatchManagerFeatures';

const { showTemplateFeature } = dispatchManagerFeatures;

export const shipmentSlice = createSlice({
    name: 'shipment',
    initialState: SHIPMENT_INITIAL_STATE,
    reducers: {
        setActiveTab: (state, action: PayloadAction<string>) => {
            state.activeTab = action.payload;
        },
        setShowShipmentModal: (state, action: PayloadAction<boolean>) => {
            state.showShipmentModal = action.payload;
        },
        setIsShipmentUpdate: (state, action) => {
            state.isShipmentUpdate = action.payload;
        },
        setNewShipmentBasicInfo: (
            state,
            action: PayloadAction<INewShipmentBasicInfo>,
        ) => {
            state.newShipment = action.payload;
        },
        setSelectedPinDropLocation: (
            state,
            action: PayloadAction<ILocation | undefined>,
        ) => {
            state.selectedPinDropLocation = action.payload;
        },
        updateNewShipmentBasicInfo: (
            state,
            action: PayloadAction<Partial<INewShipmentBasicInfo>>,
        ) => {
            state.newShipment = {
                ...state.newShipment,
                ...action.payload,
            };
        },
        setEstimateRateDetail: (
            state,
            action: PayloadAction<IEstimateRateDetail[]>,
        ) => {
            state.estimateRateList = action.payload;
        },
        setShipmentLocations: (
            state,
            action: PayloadAction<IShipmentLocation[]>,
        ) => {
            state.shipmentLocations = action.payload;
        },
        setLocations: (state, action: PayloadAction<ILocation[]>) => {
            state.locations = action.payload;
        },
        setDraftLocations: (state, action: PayloadAction<ILocation[]>) => {
            state.draftLocations = action.payload;
        },
        setLocationDetail: (
            state,
            action: PayloadAction<ILocationModalDetail>,
        ) => {
            state.locationModalDetail = action.payload;
        },
        setLocationModalDetail: (
            state,
            action: PayloadAction<Partial<ILocationModalDetail>>,
        ) => {
            state.locationModalDetail = {
                ...state.locationModalDetail,
                ...action.payload,
            };
        },
        setPindrops: (state, action: PayloadAction<ISelect[]>) => {
            state.pindrops = action.payload;
        },
        setPindropsList: (state, action: PayloadAction<IPindrop[]>) => {
            state.pindropList = action.payload;
        },
        setShowNewCustomerBrokerModal: (
            state,
            action: PayloadAction<boolean>,
        ) => {
            state.showNewCustomerBrokerModal = action.payload;
        },
        setShowEstimateRatesModal: (state, action: PayloadAction<boolean>) => {
            state.showEstimateRatesModal = action.payload;
        },
        resetAddLocationModalStates: state => {
            state.locationModalDetail =
                SHIPMENT_INITIAL_STATE.locationModalDetail;
            state.pindrops = SHIPMENT_INITIAL_STATE.pindrops;
            state.pindropList = SHIPMENT_INITIAL_STATE.pindropList;
        },
        resetLocations: state => {
            const locationDetailCopy = deepClone(state.locations);

            locationDetailCopy[0] = SHIPMENT_INITIAL_STATE.locations[0];
            locationDetailCopy[1] = SHIPMENT_INITIAL_STATE.locations[1];

            for (let i = 2; i < locationDetailCopy.length; i++) {
                locationDetailCopy[i] = LocationDetailConstants.newLocation;
            }

            state.locations = locationDetailCopy;
            state.newShipment.estimatedDistance =
                SHIPMENT_INITIAL_STATE.newShipment.estimatedDistance;
        },
        resetAllShipmentStates: state => {
            const nextState: IShipmentInitialState = {
                ...SHIPMENT_INITIAL_STATE,
                isSubmitting: false,
                showShipmentModal: state.showShipmentModal,
                commodityList: state.commodityList,
                pindropList: state.pindropList,
                pindrops: state.pindrops,
            } as IShipmentInitialState;

            if (showTemplateFeature) {
                nextState.templateCreationData = state.templateCreationData;
                nextState.templateCreationName = state.templateCreationName;
                nextState.showCreateTemplateModal =
                    state.showCreateTemplateModal;
                nextState.shipmentTemplates = state.shipmentTemplates;
                nextState.selectedTemplate = state.selectedTemplate;
                nextState.lastAppliedTemplateId = state.lastAppliedTemplateId;
                nextState.lastAppliedTemplateName =
                    state.lastAppliedTemplateName;
                nextState.lastAppliedTemplateCustomerId =
                    state.lastAppliedTemplateCustomerId;
            }

            return nextState;
        },
        setShipmentRecord: (state, action: PayloadAction<IShipmentRecord>) => {
            state.shipmentRecord = action.payload;
        },
        setCanResetTime: (state, action: PayloadAction<boolean>) => {
            state.canResetTime = action.payload;
        },
        setShipmentDetailPayload: (
            state,
            action: PayloadAction<Partial<IShipmentRecord>>,
        ) => {
            state.shipmentRecord = {
                ...state.shipmentRecord,
                ...action.payload,
            };
        },
        setCommodityList: (state, action: PayloadAction<ISelect[]>) => {
            state.commodityList = action.payload;
        },
        setCommodityModalDetail: (
            state,
            action: PayloadAction<ICommodityModalDetail>,
        ) => {
            state.commodityModalDetail = action.payload;
        },
        setCommodityModalErrorDetail: (
            state,
            action: PayloadAction<ICommodityModalErrorDetail>,
        ) => {
            state.commodityModalErrorDetail = action.payload;
        },
        setCommodityDetailPayload: (
            state,
            action: PayloadAction<Partial<ICommodityModalDetail>>,
        ) => {
            state.commodityModalDetail = {
                ...state.commodityModalDetail,
                ...action.payload,
            };
        },
        setCommodityErrorDetailPayload: (
            state,
            action: PayloadAction<Partial<ICommodityModalErrorDetail>>,
        ) => {
            state.commodityModalErrorDetail = {
                ...state.commodityModalErrorDetail,
                ...action.payload,
            };
        },
        setLongestRoute: (
            state,
            action: PayloadAction<google.maps.LatLngLiteral[]>,
        ) => {
            state.longestRoute = action.payload;
        },
        addLocation: state => {
            state.locations = [
                ...state.locations,
                LocationDetailConstants.newLocation,
            ];
        },
        removeLocation: (state, action: PayloadAction<number>) => {
            state.locations = [
                ...state.locations.filter(
                    (_locationDetail, locationIndex) =>
                        locationIndex !== action.payload,
                ),
            ];
        },
        setShipmentTemplates: (
            state,
            action: PayloadAction<IShipmentTemplate[]>,
        ) => {
            state.shipmentTemplates = action.payload;
        },
        setSelectedTemplate: (
            state,
            action: PayloadAction<IShipmentTemplate | null>,
        ) => {
            state.selectedTemplate = action.payload;
        },
        setShowTemplateModal: (state, action: PayloadAction<boolean>) => {
            state.showTemplateModal = action.payload;
        },
        applyTemplate: (state, action: PayloadAction<IShipmentTemplate>) => {
            const template = action.payload;

            state.selectedTemplate = template;

            state.lastAppliedTemplateId = template.templateId ?? null;
            state.lastAppliedTemplateName = template.name ?? null;
            state.lastAppliedTemplateCustomerId = template.customerId ?? null;

            const mapped = mapTemplateToShipmentParts(template, {
                currentLocations: state.locations,
                pindrops: state.pindropList,
            });

            state.newShipment = {
                ...SHIPMENT_INITIAL_STATE.newShipment,
                ...mapped.newShipment,
            } as INewShipmentBasicInfo;
            state.shipmentRecord = {
                ...SHIPMENT_INITIAL_STATE.shipmentRecord,
                ...mapped.shipmentRecord,
            } as IShipmentRecord;
            if (mapped.locations && mapped.locations.length > 0) {
                state.locations = mapped.locations;
            } else {
                state.locations = SHIPMENT_INITIAL_STATE.locations;
            }
        },
        setShowDeleteBuildShipmentModal: (
            state,
            action: PayloadAction<boolean>,
        ) => {
            state.showDeleteShipmentModal = action.payload;
        },
        setShowCreateTemplateModal: (state, action: PayloadAction<boolean>) => {
            state.showCreateTemplateModal = action.payload;
        },
        clearLastAppliedTemplate: state => {
            state.lastAppliedTemplateId = null;
            state.lastAppliedTemplateName = null;
            state.lastAppliedTemplateCustomerId = null;
        },
        setTemplateCreationData: (
            state,
            action: PayloadAction<ITemplateCreationData | null>,
        ) => {
            state.templateCreationData = action.payload;
        },
        setTemplateCreationName: (state, action: PayloadAction<string>) => {
            state.templateCreationName = action.payload;
        },
        clearTemplateCreation: state => {
            state.showCreateTemplateModal = false;
            state.templateCreationData = null;
            state.templateCreationName = '';
        },
        setCreatedShipmentId: (state, action: PayloadAction<number | null>) => {
            state.createdShipmentId = action.payload;
        },
        setLastAppliedTemplateContext: (
            state,
            action: PayloadAction<{
                templateId?: number | null;
                templateName?: string | null;
                customerId?: number | null;
            }>,
        ) => {
            const { templateId, templateName, customerId } = action.payload;
            state.lastAppliedTemplateId = templateId ?? null;
            state.lastAppliedTemplateName = templateName ?? null;
            state.lastAppliedTemplateCustomerId = customerId ?? null;
        },
    },
});

export const {
    setActiveTab,
    setShowShipmentModal,
    setNewShipmentBasicInfo,
    updateNewShipmentBasicInfo,
    setShipmentLocations,
    setLocations,
    setDraftLocations,
    setLocationModalDetail,
    setPindrops,
    setPindropsList,
    setLocationDetail,
    resetAddLocationModalStates,
    resetAllShipmentStates,
    resetLocations,
    setEstimateRateDetail,
    setShowNewCustomerBrokerModal,
    setShowEstimateRatesModal,
    setShipmentRecord,
    setShipmentDetailPayload,
    setCanResetTime,
    setCommodityList,
    setCommodityModalDetail,
    setCommodityModalErrorDetail,
    setCommodityDetailPayload,
    setCommodityErrorDetailPayload,
    setLongestRoute,
    addLocation,
    removeLocation,
    setSelectedPinDropLocation,
    setIsShipmentUpdate,
    setShowDeleteBuildShipmentModal,
    setShipmentTemplates,
    setSelectedTemplate,
    setShowTemplateModal,
    applyTemplate,
    setShowCreateTemplateModal,
    setTemplateCreationData,
    setTemplateCreationName,
    clearTemplateCreation,
    clearLastAppliedTemplate,
    setLastAppliedTemplateContext,
    setCreatedShipmentId,
} = shipmentSlice.actions;

const ShipmentReducer = shipmentSlice.reducer;
export default ShipmentReducer;
